import { FormField, Select } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { listConnectorIntegrationsOptions, listConnectorTemplatesOptions } from "@/api";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { ConnectorForm } from "./ConnectorForm";
import { IntegrationInstanceForm } from "./IntegrationInstanceForm";
import { TemplateConnectorForm } from "./TemplateConnectorForm";

const SERVER = "server";

/** /build/connectors/new: an MCP server or API endpoint, or my own connector for an integration (?integration=). */
export function NewConnectorPage() {
  const { t } = useTranslation("connectors");
  const areas = useAreaCrumbs();
  const pickId = useId();
  const [params, setParams] = useSearchParams();
  const integrations = useQuery({ ...listConnectorIntegrationsOptions(), select: (r) => r.data });
  const templates = useQuery({ ...listConnectorTemplatesOptions(), select: (r) => r.data });
  const chosen = params.get("template") ? `t:${params.get("template")}` : (params.get("integration") ?? SERVER);
  const integration = (integrations.data ?? []).find((i) => i.slug === chosen);
  const template = (templates.data ?? []).find((tpl) => `t:${tpl.slug}` === chosen);
  const pick = (value: string) => {
    if (value === SERVER) setParams({}, { replace: true });
    else if (value.startsWith("t:")) setParams({ template: value.slice(2) }, { replace: true });
    else setParams({ integration: value }, { replace: true });
  };

  let picked = SERVER;
  if (template) picked = `t:${template.slug}`;
  else if (integration) picked = integration.slug;
  let form = <ConnectorForm />;
  if (template) form = <TemplateConnectorForm key={template.slug} template={template} />;
  else if (integration) form = <IntegrationInstanceForm key={integration.slug} integration={integration} />;

  return (
    <div className="flex flex-col">
      <PageBreadcrumbs
        trail={[areas.build, { label: t("title"), to: "/build/connectors" }]}
        current={t("form.create")}
      />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6 md:px-8">
        {((integrations.data ?? []).length > 0 || (templates.data ?? []).length > 0) && (
          <FormField label={t("instance.pick")} htmlFor={pickId}>
            <Select
              id={pickId}
              aria-label={t("instance.pick")}
              value={picked}
              options={[
                ...(templates.data ?? []).map((tpl) => ({
                  value: `t:${tpl.slug}`,
                  label: t("template.option", { name: tpl.name }),
                })),
                { value: SERVER, label: t("instance.server") },
                ...(integrations.data ?? []).map((i) => ({
                  value: i.slug,
                  label: t("instance.option", { integration: i.name }),
                })),
              ]}
              onChange={pick}
            />
          </FormField>
        )}
        {form}
      </div>
    </div>
  );
}
